import { Component } from '@angular/core';
import { UserService } from '../../../user-management/services/user.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { MessageService } from '../../../project-intake/services/message.service';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserLogin, UserRoleApprovalRequest, RolePermission } from '../../../Common/models/user.login';
import { MasterService } from '../../services/master.service';
import { FormGroup } from '@angular/forms';
import { RoleName } from '../../../Common/Constants/common.constants';
import { UserRole } from '../../../Common/models/user.role';
import { StorageService } from '../../../Common/services/storage.service';
import { ConfirmationService } from 'primeng/api';
import { AdminConstants } from '../../constants/AdminConstants';

interface Organization {
  name: string;
  code: string;
}

@Component({
  selector: 'app-access-management',
  templateUrl: './access-management.component.html',
  styleUrls: ['./access-management.component.css', './../../../../assets/css/master_management.css'],
})
export class AccessManagementComponent {
  allUsers: any[] = [];
  allUsersWithoutDeleted: any[] = [];
  filteredUsers: any[] = [];
  searchTerm?: string;

  selectedUserStatus: string = '0';
  selectedApprovalStatus?: string = '0';
  selectedUserApprovalStatus?: string = '0';
  selectedUserRole: string = '0';

  approvalStatuses = [
    { value: 'pending', label: 'PENDING' },
    { value: 'approved', label: 'APPROVED' },
    { value: 'rejected', label: 'REJECTED' }
  ];

  userStatuses = [
    { value: 'yes', label: 'Active' },
    { value: 'no', label: 'Disabled' }
  ];

 
  uInfo: UserLogin | null | undefined;

  categories!: any[];
  //selectedpermission: any = "";
  permissions: any[] = [
    { name: 'Read', key: 'Read' },
    { name: 'Write', key: 'Write' },

  ];
  organizations: Organization[] | undefined;
  selectedOrganization: Organization | undefined;
  roles: string[] = ['Admin', 'SRE Support', 'Customer Support'];
  isEditUserRoles: boolean = false;

  selectedUserName: string = '';
  selectedUserEmail: string = '';
  selectedUser!: any;
  selectedUserId: number = 0;

  selectedPermission!: any;
  selectedCategories: any[] = [];

  public isSREAdmin: boolean = false;
  public isITAdmin: boolean = false;
  public isSRETeam: boolean = false;
  public isSRESupport: boolean = false;
  public isSRESupportRead: boolean = false;
  public isSRESupportWrite: boolean = false;
  public isCustomerSupportTeam: boolean = false;

  disableExportUsersListAction: boolean = false;

  userFilterRoles = [
    { value: 'SRE Admin', label: 'ISO ADMIN' },
    { value: 'Customer Team', label: 'CUSTOMER SUPPORT' }
  ];

  selectedFilterCategories: any[] = [];

  constructor(private userService: UserService,
    private msgService: MessageService,
    private masterService: MasterService,
    private storageService: StorageService,
    private confirmationService: ConfirmationService,
    private ngxService: NgxUiLoaderService) { }

  ngOnInit(): void {
    this.uInfo = this.storageService.getItem<UserLogin>("UserInfo");
    this.disableExportUsersListAction = false;

    this.getUsers();
    this.getCategories();
  }

  getUsers(): void {
    this.ngxService.start();
    this.userService.getAllUsersWithRoles()
      .pipe(
        catchError(error => {
          this.ngxService.stop();
          return of([]);
        })
      )
      .subscribe(users => {
        this.allUsers = users.sort((a, b) => b.userID! - a.userID!);
        this.allUsersWithoutDeleted = this.allUsers.filter(user => user.status !== 'DELETED');
        this.filteredUsers = this.allUsers
          .filter(user => user.status !== 'DELETED')
          .sort((a, b) => this.getStatusOrder(a.status) - this.getStatusOrder(b.status));

        this.ngxService.stop();
      });
  }

  getStatusOrder(status: string): number {
    switch (status) {
      case 'PENDING':
        return 0;
      case 'APPROVED':
        return 1;
      case 'REJECTED':
        return 2;
      default:
        return 3; // for any other status (like null, etc.)
    }
  }

  getCategories(): void {
    this.masterService.getAllCategories(1).subscribe(response => {
      const categories = response.map((item: any) => ({
        name: item.categoryName.trim(),
        code: item.categoryID
      }));
      this.categories = categories;
    });
  }

  filterUsers(): void {
    const term = this.searchTerm?.toLowerCase();
    if (term === undefined) {
      this.msgService.htmlError('Please enter search value');
      return;
    }
    this.allUsers = this.allUsers.filter(user => user.status !== 'DELETED');
    this.filteredUsers = this.allUsers
      .filter(user =>
        user.status !== 'DELETED' &&
        [`${user.firstname} ${user.lastname}`, user.userEmail].some(field =>
          field.toLowerCase().includes(term.toLowerCase())
        )
      )
      .sort((a, b) => this.getStatusOrder(a.status) - this.getStatusOrder(b.status));
  }

  onApprovalStatusChange(value: string) {
    this.selectedApprovalStatus = value;

    if (value === 'pending' || value === 'rejected') {
      this.selectedUserStatus = '0';
    }
  }

  isRetailHubStatusDisabled(): boolean {
    return this.selectedApprovalStatus === 'pending' || this.selectedApprovalStatus === 'rejected';
  }

  onApplyFilter(): void {
    this.filteredUsers = this.allUsers
      .filter(user =>
        this.matchesUserStatus(user) &&
        this.matchesApprovalStatus(user) &&
        this.matchesRoleFilter(user) &&
        this.matchesCategoryFilter(user)
      )
      .sort((a, b) => this.getStatusOrder(a.status) - this.getStatusOrder(b.status));
  }

  private matchesUserStatus(user: any): boolean {
    if (this.selectedUserStatus === '0') return true;
    const userStatus = this.selectedUserStatus === 'yes';
    return user.isActive === userStatus;
  }

  private matchesApprovalStatus(user: any): boolean {
    if (this.selectedApprovalStatus === '0') return true;
    return user.status.toLowerCase() === this.selectedApprovalStatus;
  }

  private matchesRoleFilter(user: any): boolean {
    if (!this.selectedUserRole || this.selectedUserRole === '0') return true;

    const userRoles = user.selectedRoles?.toLowerCase().split(',').map((r: string) => r.trim());
    return userRoles?.includes(this.selectedUserRole.toLowerCase());
  }

  private matchesCategoryFilter(user: any): boolean {
    if (!this.selectedFilterCategories || this.selectedFilterCategories.length === 0) return true;

    const userCategories = user.selectedCategories?.toLowerCase().split(',').map((c: string) => c.trim());
    return this.selectedFilterCategories.some(selected =>
      userCategories?.includes(selected.toLowerCase())
    );
  }

  shouldShowCategories(selectedRoles: string, selectedCategories: string): boolean {
    if (!selectedRoles || !selectedCategories) return false;

    const roles = selectedRoles
      .split(',')
      .map(r => r.trim());

    const hasCustomerTeam = roles.includes('Customer Team');
    const hasSreAdmin = roles.includes('SRE Admin');

    // Show category only if it has Customer Team, does NOT have SRE Admin, and categories exist
    return hasCustomerTeam && !hasSreAdmin && selectedCategories.trim() !== '';
  }

  enableOrDisableUserStatus(event: any, user: any): void {
    user.isActive = event.target.checked;
    user.approvedOrRejectedBy = this.uInfo!.userEmail;

    const userRoleApprovalRequest: UserRoleApprovalRequest = {
      user: user,
      roles: []
    };

    this.updateApprovalOrUserStatus(userRoleApprovalRequest);
  }

  approveOrRejectApprovalStatus(user: any, isApprove: boolean): void {
    if (isApprove) {
      user.isActive = true;
      user.status = 'APPROVED';
    }
    else {
      user.isActive = false;
      user.status = 'REJECTED';
    }
    user.approvedOrRejectedBy = this.uInfo!.userEmail;

    const userRoleApprovalRequest: UserRoleApprovalRequest = {
      user: user,
      roles: []
    };

    if (this.isSREAdmin) {
      const adminRole: RolePermission = {
        roleID: 2,
        read: true,
        write: true,
        categoryID: 0
      };
      userRoleApprovalRequest.roles.push(adminRole);
    }

    if (this.isSRESupport) {

      if (this.selectedPermission == 'Read') {
        this.isSRESupportRead = true;
      }

      if (this.selectedPermission == 'Write') {
        this.isSRESupportWrite = true;
      }

      const sreSupportRole: RolePermission = {
        roleID: 3,
        read: this.isSRESupportRead,
        write: this.isSRESupportWrite,
        categoryID: 0
      };
      userRoleApprovalRequest.roles.push(sreSupportRole);
    }

    if (this.isCustomerSupportTeam) {

      if (this.selectedCategories.length == 0) {
        this.msgService.htmlError('Please select atleast one category');
        return;
      }
      this.selectedCategories.forEach((categoryId: any) => {

        const customerSupportRole: RolePermission = {
          roleID: 4,
          read: true,
          write: true,
          categoryID: categoryId
        };

        userRoleApprovalRequest.roles.push(customerSupportRole);
      });
    }

    this.updateApprovalOrUserStatus(userRoleApprovalRequest);
  }

  updateApprovalOrUserStatus(userRoleApprovalRequest: any): void {
    this.userService.updateApprovalOrUserStatus(userRoleApprovalRequest).subscribe({
      next: (result) => {
        this.msgService.htmlSuccess('User status updated successfully');
        this.getUsers();
        this.resetSearchAndFilters();
        this.cancelUserDetails();
      },
      error: () => this.msgService.htmlError('Failed to update user status'),
      complete: () => {
      },
    });
  }

  private async resetSearchAndFilters() {
    this.searchTerm = '';
    this.selectedUserStatus = '0';
    this.selectedApprovalStatus = '0';
  }

  cancelUserDetails() {
    this.selectedCategories = [];

    this.isSREAdmin = false;
    this.isITAdmin = false;
    this.isSRETeam = false;
    this.isSRESupport = false;
    this.isSRESupportRead = false;
    this.isSRESupportWrite = false;
    this.isCustomerSupportTeam = false;

    this.selectedUserName = '';
    this.selectedUserEmail = '';
    this.isSRESupportRead = false;
    this.isSRESupportWrite = false;
    this.selectedPermission = '';

    this.isEditUserRoles = false;
  }

  editUserDetails(selectedUser: any) {
    this.selectedUserName = selectedUser.firstname + ' ' + selectedUser.lastname;
    this.selectedUserEmail = selectedUser.userEmail;
    this.selectedUser = selectedUser;
    this.selectedUserApprovalStatus = selectedUser.status;
    this.ngxService.start();
    this.userService.getUserRoleInfo(selectedUser.userID)
      .pipe(
        catchError(error => {
          this.ngxService.stop();
          return of([]);
        })
      )
      .subscribe((userRoles: UserLogin | never[]) => {

        if (!Array.isArray(userRoles) && userRoles.roles) {
          this.isEditUserRoles = true;

          this.isSREAdmin = userRoles.roles.some((a: UserRole) => a.roleName === RoleName.SREAdmin);
          this.isSRESupport = userRoles.roles.some((a: any) => (a.roleName == RoleName.SRESupport));
          this.isSRESupportRead = userRoles.roles.some((a: any) => (a.roleName == RoleName.SRESupport) && (a.read == true));
          this.isSRESupportWrite = userRoles.roles.some((a: any) => (a.roleName == RoleName.SRESupport) && (a.write == true));
          this.isCustomerSupportTeam = userRoles.roles.some((a: any) => a.roleName == RoleName.CustomerTeam);

          if (this.isSRESupport && this.isSRESupportRead) {
            this.selectedPermission = 'Read';
          }
          if (this.isSRESupport && this.isSRESupportWrite) {
            this.selectedPermission = 'Write';
          }
          if (!this.isSRESupport) {
            this.selectedPermission = '';
          }

          if (this.isCustomerSupportTeam && userRoles.userCategory !== undefined && userRoles.userCategory !== null) {
            this.selectedCategories = userRoles.userCategory!.map(a => a.categoryID);
          }
          else {
            this.selectedCategories = [];
          }
        } else {
          this.isSREAdmin = false;
          this.isSRESupport = false;
          this.isCustomerSupportTeam = false;
          this.selectedPermission = '';
        }
        this.ngxService.stop();
      });
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  deleteUserDetails(selectedUser: any) {
    this.confirmationService.confirm({
      header: "Delete User",
      message: "Are you sure you want to delete the user?\n This action will permanently remove the user from the system",
      accept: () => {
        this.userService.deleteUser(selectedUser.userID).subscribe(data => {
          this.msgService.htmlSuccess('User deleted successfully');
          this.getUsers();
          this.resetSearchAndFilters();
          this.cancelUserDetails();
        });
      },
      reject: () => {
      }
    });
  }

  exportUsersList() {
    if (this.filteredUsers.length > 0) {
      this.disableExportUsersListAction = true;
      const userIds: number[] = this.filteredUsers.map(user => user.userID);

      this.userService.GetUsersListReport(userIds).subscribe({
        next: (result) => {
          this.downloadFile(result);
          this.msgService.htmlSuccess(AdminConstants.EXPORT_USERSLIST_SUCCESS);
        },
        error: () => this.msgService.htmlError(AdminConstants.EXPORT_USERSLIST_ERROR),
        complete: () => {
          this.disableExportUsersListAction = false;
        },
      });

    }
    else {
      this.msgService.htmlError(AdminConstants.EXPORT_USERSLIST_NOTAVAILABLE);
    }
  }

  downloadFile(blob: Blob): void {
    if (blob) {
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;

      const now = new Date();
      let reportFileName = `UsersList`;
      a.download = `${reportFileName}.xlsx`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } else {
      this.msgService.htmlError(AdminConstants.EXPORT_USERSLIST_DOWNLOADERROR);
    }
  }
}